import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { Correct } from '../../components/Correct'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { Exemplar } from './Exemplar'
import { LABEL_SOURCE_NOTE, type LabelSource, type ScenarioIndexEntry, type ScenarioView } from '../../types/voice'

// SEEING INTO ONE SCENARIO. Before this the only thing behind a scenario's number was eight examples;
// "everything else" held 3,898 of the first real user's 9,070 messages and no screen showed him one of
// them. So: the examples first, with the line saying why each is there, then every message the scenario
// holds, newest first and paged — each row saying plainly who decided its label, because most of them
// were decided by a regex cue table before any model read the mail, and a guess must not be shown as a
// measurement. Every row and every example can be argued with.

const PAGE = 25

export function ScenarioPane({ scenarioKey, view, entry }: { scenarioKey: string; view?: ScenarioView; entry?: ScenarioIndexEntry }) {
  const spoke = useSpoke()
  const [page, setPage] = useState(1)
  useEffect(() => { setPage(1) }, [scenarioKey])

  const isAll = scenarioKey === 'all'
  const messages = useQuery({
    queryKey: ['voice-scenario-messages', spoke, scenarioKey, page],
    queryFn: () => voiceApi.scenarioMessages(spoke, scenarioKey, page, PAGE),
    enabled: !isAll,
  })
  const refusal = messages.error instanceof RefusedError ? messages.error.refusal : null
  const total = messages.data?.total ?? entry?.messageCount ?? view?.messageCount ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))

  return (
    <>
      {view ? (
        <div className="card">
          <div className="card-head">
            <div>
              <h2>In your own words</h2>
              <p className="muted">
                {view.exemplars.length} messages chosen for range, not similarity: as different from one another as your mail gets while still being you.
                This is what a model is shown when it drafts for you here. {view.exemplarNote}
              </p>
            </div>
          </div>
          <div className="exemplars">
            {view.exemplars.map((e) => <Exemplar key={e.id} e={e} />)}
          </div>
        </div>
      ) : (
        <div className="card">
          <p className="muted">
            No examples have been chosen for this scenario: it holds mail, but the last build did not make a profile of
            its own for it. Read new mail to build one.
          </p>
        </div>
      )}

      {!isAll && (
        <div className="card scenario-messages">
          <div className="card-head">
            <div>
              <h2>Every message in this scenario</h2>
              <p className="muted">
                {total.toLocaleString()} {total === 1 ? 'message' : 'messages'}, newest first. <ProvenanceLine entry={entry} />
              </p>
            </div>
          </div>

          {messages.isPending && <p className="muted">Loading…</p>}
          {refusal && <p className="error">{refusal.message}</p>}

          {messages.data && messages.data.messages.length === 0 && (
            <p className="muted">Nothing is filed here yet.</p>
          )}

          {messages.data && messages.data.messages.length > 0 && (
            <ul className="scenario-list">
              {messages.data.messages.map((m) => (
                <li key={m.id} className={`scenario-row ${m.isExemplar ? 'is-exemplar' : ''}`}>
                  <p className="scenario-row-meta">
                    <span>{formatDate(m.sentAt)}</span>
                    <span className="scenario-row-subject">{m.subject || '(no subject)'}</span>
                    {m.isExemplar && <span className="tag">an example</span>}
                    {m.isRewritten && <span className="tag">rewritten by you</span>}
                    <Correct kind="voice-scenario" entityId={m.id} entityLabel={m.subject ?? '(no subject)'} compact className="scenario-row-correct" />
                  </p>
                  <p className="scenario-row-head">{m.head}</p>
                  <p className="scenario-row-why">
                    <span>{m.words.toLocaleString()} words</span>
                    <span>{m.isReply ? 'reply' : 'opener'}</span>
                    {m.kind && m.kind !== 'message' && <span>{m.kind}</span>}
                    <LabelSourceWords source={m.labelSource} />
                  </p>
                </li>
              ))}
            </ul>
          )}

          {pages > 1 && (
            <div className="scenario-pager">
              <button type="button" className="button small" disabled={page <= 1 || messages.isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>Newer</button>
              <span className="muted small">Page {page} of {pages.toLocaleString()}</span>
              <button type="button" className="button small" disabled={page >= pages || messages.isFetching} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Older</button>
            </div>
          )}
        </div>
      )}
    </>
  )
}

/** How this scenario's labels were decided, in numbers. Never rounded into "measured". */
function ProvenanceLine({ entry }: { entry?: ScenarioIndexEntry }) {
  if (!entry) return null
  const parts: string[] = []
  if (entry.modelSorted > 0) parts.push(`${entry.modelSorted.toLocaleString()} sorted by a model`)
  if (entry.regexClassified > 0) parts.push(`${entry.regexClassified.toLocaleString()} ${LABEL_SOURCE_NOTE}`)
  if (entry.youFiled > 0) parts.push(`${entry.youFiled.toLocaleString()} filed by you`)
  if (parts.length === 0) return null
  return <span>{parts.join(' · ')}.</span>
}

/** Who said so, in the person's language. A pattern match is named as one. */
export function LabelSourceWords({ source }: { source: LabelSource }) {
  if (source === 'model') return <span className="label-source model">sorted by a model</span>
  if (source === 'you') return <span className="label-source you">filed by you</span>
  return <span className="label-source regex" title="No model has read this message yet. The scenario is a cue-table match on its words, not a reading of it.">{LABEL_SOURCE_NOTE}</span>
}
